<script setup lang="ts">
import { areasIcons } from '~/assets/js/areasIcons'
import type { HouseAreaEquipment, Locale } from 'lc-services/types'

type HouseArea = HouseAreaEquipment['areas'] extends Array<infer T> ? T : never
type HouseEquipment = HouseArea['equipments'] extends Array<infer T> ? T : never

const props = defineProps<{
  areaEquipments: HouseAreaEquipment[]
}>()

const { locale, t } = useI18n()
const { isMobile } = useBreakpoint()

const tabs = computed(() =>
  props.areaEquipments.map((group) => group.name[locale.value as Locale]),
)
const getEquipments = (tabIndex: number) => {
  const value = tabs.value[tabIndex]

  return props.areaEquipments.find(
    (group) => group.name[locale.value as Locale] === value,
  )
}

const poolHeaterAndTreatment = (pool: HouseEquipment) => {
  const array = []
  const poolHeater = pool.poolHeater?.[locale.value]
  const poolTreatment = pool.poolTreatment?.[locale.value]

  if (poolHeater) array.push(poolHeater)
  if (poolTreatment) array.push(poolTreatment)

  return array.join(' · ')
}

const poolOptions = (pool: HouseEquipment) => {
  if (pool.poolOptions) {
    return pool.poolOptions.map((pool) => pool[locale.value]).join(' · ')
  }

  return ''
}

const poolDimension = (pool: HouseEquipment) => {
  let dimension = ''
  if (pool.poolLength || pool.poolWidth)
    dimension += `${t('house.rooms.sizes')} : `
  if (pool.poolLength) dimension += `L = ${pool.poolLength}`
  if (pool.poolLength && pool.poolWidth) dimension += `, l = ${pool.poolWidth}`
  if (!pool.poolLength && pool.poolWidth) dimension += ` l = ${pool.poolWidth}`
  // Third line depthMin + depthMax
  if (
    (pool.poolDepthMin || pool.poolDepthMax) &&
    (pool.poolLength || pool.poolWidth)
  )
    dimension += `, `
  if (pool.poolDepthMin || pool.poolDepthMax)
    dimension += `${t('house.rooms.depth')} = `
  if (pool.poolDepthMin) dimension += `${pool.poolDepthMin}`
  if (pool.poolDepthMin && pool.poolDepthMax) dimension += ' / '
  if (pool.poolDepthMax) dimension += `${pool.poolDepthMax}`

  return dimension
}

const getIconName = (identifier: string) =>
  areasIcons.find((a) => a.identifier === identifier)?.icon || 'sparkles'

const formatColumns = (equipments: HouseArea['equipments']) => {
  const columns: HouseArea['equipments'][] = []
  const columnsNumber = isMobile.value ? 2 : 3
  const itemsPerColumnMax = Math.ceil(equipments.length / columnsNumber)

  for (let i = 0; i < columnsNumber; i++) columns.push([])

  columns[0].push(...equipments.slice(0, itemsPerColumnMax))

  if (isMobile.value) {
    columns[1].push(...equipments.slice(itemsPerColumnMax, equipments.length))
  } else {
    const sliceIndex =
      equipments.length % 3 === 1
        ? itemsPerColumnMax * 2 - 1
        : itemsPerColumnMax * 2

    columns[1].push(...equipments.slice(itemsPerColumnMax, sliceIndex))
    columns[2].push(...equipments.slice(sliceIndex, equipments.length))
  }

  return columns
}
</script>

<template>
  <div>
    <div v-if="isMobile" class="mb-6 font-bold">
      {{ $t('house.all_equipments') }}
    </div>
    <base-tabs :tabs="tabs">
      <template #default="{ tabIndex }">
        <div>
          <div
            v-for="area in getEquipments(tabIndex)?.areas"
            :key="`${area.name[locale]}`"
            :data-id="area.identifier"
            class="mb-6 border-b border-gray-200 pb-6 last:mb-0 last:border-none last:pb-0"
          >
            <div class="mb-2 flex items-center">
              <BaseIcon
                class="mr-2 shrink-0"
                :name="getIconName(area.identifier)"
                :size="2"
              />
              <span v-if="area.name" class="font-bold">
                {{ area.name[locale] }}
              </span>
            </div>

            <div class="mt-4 flex flex-wrap items-center">
              <span
                v-if="area.view"
                class="uppercase-first-letter mb-2 mr-2 rounded-full bg-gray-100 p-2 text-xs font-medium leading-4 text-secondary-500"
              >
                {{ area.view[locale] }}
              </span>

              <span
                v-for="kitchen in area.kitchenType"
                :key="`${locale in kitchen ? kitchen[locale] : 'undefined'}`"
                class="mb-2 mr-2 rounded-full bg-gray-100 p-2 text-xs font-medium leading-4 text-secondary-500"
              >
                {{ kitchen[locale] }}
              </span>

              <span
                v-if="area.bathType"
                class="mb-2 mr-2 rounded-full bg-gray-100 p-2 text-xs font-medium leading-4 text-secondary-500"
              >
                {{ area.bathType[locale] }}
              </span>

              <span
                v-if="area.airConditioning"
                class="mb-2 mr-2 rounded-full bg-gray-100 p-2 text-xs font-medium leading-4 text-secondary-500"
              >
                {{ $t('house.airConditioning') }}
              </span>

              <span
                v-for="garden in area.gardenType"
                :key="`${locale in garden ? garden[locale] : 'undefined'}`"
                class="mb-2 mr-2 rounded-full bg-gray-100 p-2 text-xs font-medium leading-4 text-secondary-500"
              >
                {{ garden[locale] }}
              </span>
            </div>

            <div class="grid grid-cols-2 md:grid-cols-3">
              <div
                v-for="(column, columnIndex) in formatColumns(area.equipments)"
                :key="columnIndex"
              >
                <template v-for="equipment in column">
                  <div
                    v-if="equipment.name"
                    :key="`${
                      equipment.name ? equipment.name[locale] : 'undefined'
                    }`"
                    class="pt-2"
                  >
                    <div>
                      <span v-if="equipment.quantity > 1">
                        {{ equipment.quantity }}
                      </span>
                      {{ equipment.name[locale] }}

                      <div
                        v-if="equipment.capacity"
                        class="block text-md text-gray-500"
                      >
                        {{
                          $t(
                            'house.rooms.equipment_capacity',
                            { capacity: equipment.capacity },
                            equipment.capacity,
                          )
                        }}
                      </div>

                      <div
                        v-if="equipment.brand"
                        class="block text-md text-gray-500"
                      >
                        {{ equipment.brand }}
                      </div>

                      <div
                        v-if="equipment.heatingType"
                        class="block text-md text-gray-500"
                      >
                        {{ equipment.heatingType[locale] }}
                      </div>

                      <div
                        v-if="equipment.bedSize"
                        class="block text-md text-gray-500"
                      >
                        {{ equipment.bedSize }}
                      </div>
                      <div
                        v-if="equipment.bedType"
                        class="block text-md text-gray-500"
                      >
                        {{ equipment.bedType[locale] }}
                      </div>

                      <div
                        v-if="equipment.identifier === 'pool'"
                        class="block text-md text-gray-500"
                      >
                        <div v-if="poolOptions(equipment)">
                          {{ poolOptions(equipment) }}
                        </div>
                        <div v-if="poolHeaterAndTreatment(equipment)">
                          {{ poolHeaterAndTreatment(equipment) }}
                        </div>
                        <div v-if="poolDimension(equipment)">
                          {{ poolDimension(equipment) }}
                        </div>
                      </div>
                    </div>
                  </div>
                </template>
              </div>
            </div>
          </div>
        </div>
      </template>
    </base-tabs>
  </div>
</template>

<style>
.column-span-all {
  column-span: all;
}
.uppercase-first-letter:first-letter {
  text-transform: uppercase;
}
</style>
